<!--src/components/nav/CommonHeader.vue-->
<script setup>
import {onMounted, onUnmounted, ref, computed, nextTick, watch} from 'vue'
import { useRouter, useRoute } from 'vue-router'
import TabbarSwitch from '@/components/Common/TabbarSwitch.vue'
import SearchInput from '@/components/Common/SearchInput.vue'
import SignStatsCard from '@/components/Common/SignStatsCard.vue'
import { DEFAULT_TAB_LIST } from '@/config/tabConfig'
import { getHotSearchTags } from '@/data/hot-search-data'
import { debounce } from 'lodash'
import { ElMessage } from "element-plus";
import {ThemeManager} from "@/utils/theme";
import { usePlatformStore } from '@/store/modules/platformStore';

const platformStore = usePlatformStore();
const isStickyNavShow = ref(false)
const topContentRef = ref(null)
const stickyTriggerHeight = ref(0)
const router = useRouter()
const route = useRoute()
const activePlatform = ref(null)

const dropdownOptions = computed(() => {
  return DEFAULT_TAB_LIST.map(platform => ({
    key: platform.dataKey,
    name: platform.name,
    path: platform.path,
    uiKey: platform.key
  }))
})

const initActivePlatform = () => {
  const platformFromQuery = route.query.platform
  if (platformFromQuery) {
    // 优先通过 dataKey 匹配（后端传递的 jd/taobao），兼容首次和切换后的参数
    let matched = DEFAULT_TAB_LIST.find(p => p.dataKey === platformFromQuery)
    // 兼容旧的 UI key 参数（兜底，防止残留错误参数）
    if (!matched) {
      matched = DEFAULT_TAB_LIST.find(p => p.key === platformFromQuery)
    }
    if (matched) {
      activePlatform.value = matched
      return
    }
  }

  let matchedPlatform = DEFAULT_TAB_LIST.find(p => p.path === route.path)
  if (!matchedPlatform && route.path === '/') matchedPlatform = DEFAULT_TAB_LIST[0]
  activePlatform.value = matchedPlatform || DEFAULT_TAB_LIST[0]
}

// --- 修复吸顶计算逻辑 ---
const calculateTriggerHeight = () => {
  if (!topContentRef.value) {
    stickyTriggerHeight.value = 0
    return
  }
  const topContentHeight = topContentRef.value.offsetHeight
  const bufferHeight = 20 // 缓冲高度，避免临界值不触发
  stickyTriggerHeight.value = topContentHeight + bufferHeight
}

const handleScroll = (() => {
  let ticking = false
  return () => {
    if (ticking) return
    ticking = true
    window.requestAnimationFrame(() => {
      const scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      // 滚动距离 >= 动态触发高度 → 显示吸顶导航
      isStickyNavShow.value = scrollTop >= stickyTriggerHeight.value
      ticking = false
    })
  }
})()

const handleResize = debounce(calculateTriggerHeight, 100)

const handleTabClick = (tab) => {
  activePlatform.value = tab

  // 更新全局平台状态
  platformStore.updatePlatform(tab);

  // 触发 SearchInput 同步切换平台（传递 tab 的 dataKey）
  const matchedDropdownPlatform = dropdownOptions.value.find(item => item.key === tab.dataKey)
  if (matchedDropdownPlatform) {
    handleSwitchPlatform(matchedDropdownPlatform)
  }

}

// 平滑滚动到顶部
const scrollToTop = () => {
  document.body.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
  isStickyNavShow.value = false
}

const handleSwitchPlatform = (platform) => {
  // 1. 匹配对应的UI平台
  const matchedUiPlatform = DEFAULT_TAB_LIST.find(p => p.dataKey === platform.key)
  if (!matchedUiPlatform) return

  // 若为当前已选平台则不重复跳转
  if (activePlatform.value?.key === matchedUiPlatform.key) return

  // 更新激活与全局状态
  activePlatform.value = matchedUiPlatform
  platformStore.updatePlatform(matchedUiPlatform);

  if (matchedUiPlatform.theme) {
    ThemeManager.switchTheme(matchedUiPlatform.theme)
  }

  // 路由处理
  if (route.path === '/searchresult') {
    router.replace({
      path: '/searchresult',
      query: {
        ...route.query,
        platform: platform.key,
        keyword: route.query.keyword || '' }
    })
  } else {
    router.push({ path: matchedUiPlatform.path })
  }
}

const handleSearch = (data) => {
  if (!data.keyword.trim()) return ElMessage.warning('请输入搜索关键词')
  // 修复：使用 dataKey 作为平台标识，确保与后端一致
  const validPlatformKey = data.platform?.key || data.platform?.dataKey || activePlatform.value?.dataKey || 'jd'
  const validKeyword = data.keyword.trim()
  router.push({
    path: '/searchresult',
    query: {
      platform: validPlatformKey,
      keyword: validKeyword
    }
  })
}

// 热搜展示逻辑
const hotSearchTags = ref(getHotSearchTags('common'))
const hotSellingItems = ref(['苹果17 Pro', '华为Mate80', 'Nike运动鞋', '海底捞火锅券', '星巴克咖啡杯', '小米手环12', '乐高积木', '三只松鼠坚果'])
const hotSearchDisplay = ref([])
const hotSellingDisplay = ref([])

const calculateDisplayCount = () => {
  const w = window.innerWidth
  let count = 8
  if (w <= 1200) count = 7
  if (w <= 1024) count = 6
  if (w <= 992) count = 0
  hotSearchDisplay.value = hotSearchTags.value.slice(0, count)
  hotSellingDisplay.value = hotSellingItems.value.slice(0, count)
}

const handleResizeDisplay = debounce(calculateDisplayCount, 100)

watch(
  () => [route.path, route.query.platform],
  () => {
    initActivePlatform()
    calculateDisplayCount()
    // 路由变化后重新计算高度，避免布局变动导致吸顶失效
    calculateTriggerHeight()

    // 路由path变化后，强制匹配对应平台，更新activePlatform
    const matchedPlatformByPath = DEFAULT_TAB_LIST.find(p => p.path === route.path)
    if (matchedPlatformByPath) {
      activePlatform.value = matchedPlatformByPath
    }
  },
  { immediate: true, deep: true }
)

watch(
  () => activePlatform.value,
  (newVal) => {
    if (newVal) {
      // 确保 dropdownOptions 与 activePlatform 同步，无额外操作，仅兜底
    }
  },
  { deep: true, immediate: true }
)

onMounted(async () => {
  await nextTick()
  initActivePlatform()
  calculateTriggerHeight()
  calculateDisplayCount()

  setTimeout(calculateTriggerHeight, 100)

  // 初始化全局平台状态（与当前 activePlatform 同步）
  if (activePlatform.value) {
    platformStore.updatePlatform(activePlatform.value);
  }


  window.addEventListener('resize', handleResize)
  window.addEventListener('resize', handleResizeDisplay)
  window.addEventListener('scroll', handleScroll, { passive: true })
  document.body.addEventListener('scroll', handleScroll, { passive: true })

  // 初始化触发一次
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('resize', handleResizeDisplay)
  window.removeEventListener('scroll', handleScroll)
  document.body.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <!-- 吸顶导航 -->
  <transition name="sticky-fade">
    <div
      v-if="isStickyNavShow"
      class="sticky-nav"
    >
      <div class="sticky-nav-inner container d-flex align-items-center">
        <div class="sticky-logo d-none d-lg-block col-lg-1">
          <img
            src="/images/logo_02.png"
            alt="Logo"
            class="img-fluid"
          >
        </div>
        <!-- 吸顶搜索容器 -->
        <div class="sticky-search-wrapper col-lg-8 col-12">
          <SearchInput
            :is-sticky="true"
            :default-key="activePlatform.dataKey"
            :current-key="activePlatform.dataKey"
            :dropdown-options="dropdownOptions"
            :keyword="route.query.keyword"
            @search="handleSearch"
            @switch-platform="handleSwitchPlatform"
          />
        </div>
      </div>
    </div>
  </transition>

  <!-- 普通导航 -->
  <div
    id="common"
    ref="topContentRef"
  >
    <div class="nav-search container">
      <div
        class="row align-items-center"
        style="flex-wrap: nowrap;"
      >
        <!-- 强制不换行 -->
        <!-- Logo -->
        <div class="col-2 col-lg-2 nav-search-logo">
          <img
            src="/images/logo.png"
            alt="Logo"
            class="img-fluid"
          >
        </div>

        <!-- 搜索区域 -->
        <div class="col-10 col-lg-8 nav-search-content">
          <SearchInput
            :is-sticky="false"
            :default-key="activePlatform.dataKey"
            :current-key="activePlatform.dataKey"
            :dropdown-options="dropdownOptions"
            :keyword="route.query.keyword"
            @search="handleSearch"
            @switch-platform="handleSwitchPlatform"
          />

          <!-- 热搜词 -->
          <div class="search-tags mt-2 d-none d-lg-block">
            <div class="d-flex flex-wrap">
              <span class="text-muted me-2">热搜：</span>
              <a
                v-for="tag in hotSearchDisplay"
                :key="tag"
                href="javascript:void(0)"
                class="tag-item me-2"
                @click="handleSearch({keyword: tag, platform: { key: activePlatform.dataKey }})"
              >{{ tag }}</a>
            </div>
            <div class="d-flex flex-wrap mt-1">
              <span class="text-muted me-2">热卖：</span>
              <a
                v-for="item in hotSellingDisplay"
                :key="item"
                href="javascript:void(0)"
                class="tag-item me-2"
                @click="handleSearch({keyword: item, platform: { key: activePlatform.dataKey }})"
              >{{ item }}</a>
            </div>
          </div>
        </div>

        <!-- 签到积分卡片 -->
        <div
          class="col-lg-2 d-none d-lg-block nav-search-ad"
        >
          <SignStatsCard />
        </div>
      </div>
    </div>

    <TabbarSwitch
      :active-key="activePlatform.key"
      @tab-click="handleTabClick"
    />
  </div>

  <transition name="backtop-fade">
    <button
      v-if="isStickyNavShow"
      class="backtop-btn"
      aria-label="返回顶部"
      @click="scrollToTop"
    >
      <i class="bi bi-arrow-up-circle-fill fs-3" />
    </button>
  </transition>
</template>

<style scoped lang="scss">
@import "@/assets/css/mixin.scss";

/* 吸顶导航核心样式 */
/* 新增过渡动画样式 */
.sticky-fade-enter-from,
.sticky-fade-leave-to {
  opacity: 0; // 进入/离开时透明
  transform: translateY(-10px); // 进入/离开时向上偏移 10px
}

.sticky-fade-enter-active,
.sticky-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease; // 过渡动画时长 0.3s
}

.sticky-nav {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  background-color: #faf3fc !important; // 和原导航背景色一致
  z-index: 9999 !important; // 层级最高，避免被覆盖
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;

  .sticky-nav-inner {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 15px 0; // 替代原search-wrapper的margin-top，统一控制上下内边距
  }

  .sticky-logo {
    display: flex;
    align-items: center;
    justify-content: end;
    padding: 0 10px;

    img {
      max-height: 40px; // 限制logo高度，与搜索框对齐
    }
  }

  .sticky-search-wrapper{
    padding: 10px;
  }
}
@media (max-width: 991.98px) {
  .sticky-nav{
    .sticky-nav-inner{
      padding: 10px 0 !important;
      margin: 0 !important;
    }
    .sticky-search-wrapper{
      padding: 10px;
    }
  }

}

/* 普通导航 */
#common {
  background: #faf3fc;
  padding-bottom: 10px;

  .nav-search {
    padding: 20px 0 10px;

    .nav-search-logo {
      display: flex;
      justify-content: center;
    }

    .nav-search-content {
      // 搜索框父容器样式
    }

    .search-tags {
      font-size: 12px;
      .tag-item { color: #666; &:hover { color: var(--active-bg-color, #ff0000); } }
    }

    .nav-search-ad {
      min-height: 80px;
      height: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

/*返回顶部按钮样式 */
.backtop-btn {
  position: fixed !important;
  bottom: 30px !important;
  right: 30px !important;
  width: 50px !important;
  height: 50px !important;
  border-radius: 50% !important;
  @include bg_color;
  @include font_color;
  border: none !important;
  box-shadow: 0 4px 12px rgba(255, 0, 0, 0.3) !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 9998 !important;
  transition: all 0.3s ease !important;

  &:hover {
    transform: scale(1.1) !important;
    box-shadow: 0 6px 16px rgba(255, 0, 0, 0.4) !important;
    @include bg_color;
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
}

// 返回顶部按钮过渡动画
.backtop-fade-enter-from,
.backtop-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.backtop-fade-enter-active,
.backtop-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

/* 小屏响应式 */
@media (max-width: 991.98px) {
  .sticky-nav {
    padding: 5px 10px;

    .sticky-search-container {
      max-width: none;
      width: 100%;
      margin: 0;
    }
  }

  #common {
    .nav-search {
      padding: 10px 5px;

      .row {
        flex-wrap: wrap !important; // 小屏下允许换行
      }

      .nav-search-logo, .nav-search-ad {
        display: none !important;
      }

      .nav-search-content {
        width: 100%;
        margin: 0 auto;
        margin-top: 8px;
      }
    }
  }
}
</style>
